import { useEffect, useMemo, useRef, useState } from "react"

/**
 * FontPicker — searchable dropdown over the curated /api/fonts catalogue.
 *
 * Each option is rendered IN ITS OWN TYPEFACE so the user can preview every
 * font at a glance. The Google Fonts CSS stylesheet for the visible options
 * is injected on demand into the document <head>; rendering 78 stylesheets
 * up-front would slow page load, so we lazy-load.
 *
 * Props:
 *   value         current font key (e.g. "playfair")
 *   onChange      (key) => void
 *   placeholder   shown when no font is selected
 *   label         optional inline label rendered above the input
 *   testId        forwarded to the trigger button for tests
 *   marathiOnly   if true, only Devanagari / Marathi fonts are shown
 */
export default function FontPicker({
  value,
  onChange,
  placeholder = "Default (Helvetica)",
  label,
  testId,
  marathiOnly = false,
}) {
  const [fonts, setFonts] = useState([])
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [loaded, setLoaded] = useState(false)
  const rootRef = useRef(null)

  // Fetch catalogue once
  useEffect(() => {
    let cancelled = false
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/fonts`
    fetch(url, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setFonts(d?.fonts || [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Inject Google Fonts stylesheet for all catalogue entries the first time
  // the picker opens — keeps cold page load fast.
  useEffect(() => {
    if (!open || loaded || !fonts.length) return
    const families = fonts
      .map((f) => f.family.replace(/ /g, "+") + ":wght@400;700")
      .join("&family=")
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`
    link.dataset.fontpicker = "true"
    document.head.appendChild(link)
    setLoaded(true)
  }, [open, loaded, fonts])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return fonts.filter((f) => {
      if (marathiOnly && !f.marathi) return false
      if (!q) return true
      return f.name.toLowerCase().includes(q)
    })
  }, [fonts, query, marathiOnly])

  const selected = fonts.find((f) => f.key === value)
  const groups = useMemo(() => {
    const g = {}
    filtered.forEach((f) => {
      const cat = f.marathi ? "Marathi (Devanagari)" : f.category || "Other"
      ;(g[cat] = g[cat] || []).push(f)
    })
    return g
  }, [filtered])

  return (
    <div ref={rootRef} className="relative">
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="w-full text-left px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm flex items-center justify-between hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span
          style={{ fontFamily: selected ? `"${selected.family}", serif` : undefined }}
          className={selected ? "text-slate-900" : "text-slate-400"}
        >
          {selected ? selected.name : placeholder}
        </span>
        <span className="text-slate-400 text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              placeholder="Search fonts…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm focus:outline-none"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            <button
              type="button"
              onClick={() => {
                onChange("")
                setOpen(false)
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-500 italic border-b border-slate-100"
            >
              {placeholder}
            </button>
            {Object.entries(groups).map(([cat, list]) => (
              <div key={cat}>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-50 sticky top-0">
                  {cat}
                </div>
                {list.map((f) => (
                  <button
                    type="button"
                    key={f.key}
                    onClick={() => {
                      onChange(f.key)
                      setOpen(false)
                    }}
                    className={`w-full text-left px-3 py-2 text-base hover:bg-blue-50 ${
                      f.key === value ? "bg-blue-50 text-blue-700" : "text-slate-800"
                    }`}
                    style={{ fontFamily: `"${f.family}", serif` }}
                    data-testid={`font-option-${f.key}`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            ))}
            {!filtered.length && (
              <p className="px-3 py-4 text-xs text-slate-400 text-center">No fonts match.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
